import { Component, EventEmitter, Inject, Injector, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { RmmCustomAlertingServiceController } from '@modules/rmm-custom-alerting-module/services/rmm-custom-alerting-controller.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { WizardStepComponent } from 'mbs-ui-kit';
import {
  CustomAlertingSettings,
  NotificationProfileTypes,
  RMMAlertTypes,
  RmmAlertingGeneralAlerts,
  RmmAlertingWindowsAlerts,
  RmmNotificationProfile,
  RmmNotificationsQueryParams,
  TypeOfSetting
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { RmmNotificationService } from 'mbs-ui-shared@services/rmm-notifications.service';
import { SelectedEntities } from 'mbs-ui-shared@store/rmmGroupActionWizard/model';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  noop,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs';
import EditRmmNotificationUtility from '../../utility/edit-rmm-notification-utility';
import { LoadRmmCustomNotificationWizardService } from '../../utility/load-rmm-custom-notification-wizard.service';

export interface ActionForm {
  action: FormControl<{
    id: number;
    key: TypeOfSetting;
    label: string;
  }>;
  profileId: FormControl<string>;
}
@UntilDestroy()
@Component({
  selector: 'mbs-select-action',
  templateUrl: './select-action.component.html'
})
export class SelectActionComponent implements OnInit {
  @Input() companySettingsID: BehaviorSubject<string> = new BehaviorSubject(null);

  @Output() onSelectedEntitiesChanged = new EventEmitter<SelectedEntities>();
  @Output() disableNext = new EventEmitter<boolean>();
  @Output() closeWizardOnRedirect = new EventEmitter();
  @Output() isCustom = new EventEmitter<boolean>();

  public actions = Object.keys(TypeOfSetting).map((value, id) => ({
    id: id,
    key: TypeOfSetting[value],
    label: this.i18n.t('mbs-ui:rmm-custom-alerting.wizard.firstStep.selectionList.' + TypeOfSetting[value])
  }));

  public actionTypes = TypeOfSetting;
  public profiles: RmmNotificationProfile[] = [];
  public typeahead$ = new Subject<string>();

  public form = new FormGroup<ActionForm>({
    action: new FormControl(this.actions[0]),
    profileId: new FormControl(null)
  });

  public isNotConfigured = false;
  public inheritedSettingsFrom = '';

  public customAlertingForm: UntypedFormGroup;
  public allGeneralAlertSelected = false;
  public allWindowsAlertSelected = false;
  public windowsAlerts = RmmAlertingWindowsAlerts;
  public generalAlerts = RmmAlertingGeneralAlerts;

  public enableLoadFromComputer = false;
  public enableLoadFromCompany = false;
  public canShowSummaryAndLink = true;
  public isInherited = false;
  public alertFromValue = '';
  public totalCount = 0;
  public loading = true;
  public windowsAlertsEnabled = true;
  public showApplyToStep = false;
  public applyToCorrect = true;
  public showCustomNotSelectAlert = false;

  public globalTypeName = this.i18n.t('mbs-ui:rmm-custom-alerting.wizard.firstStep.global');
  public computerTypeName = this.i18n.t('mbs-ui:rmm-custom-alerting.wizard.firstStep.computer');
  public companyTypeName = this.i18n.t('mbs-ui:rmm-custom-alerting.wizard.firstStep.company');

  get getSettingsTypeText(): string {
    switch (this.form.get('action').value.key) {
      case this.actionTypes.Company:
        return this.companyTypeName;
      case this.actionTypes.Computer:
        return this.computerTypeName;
      case this.actionTypes.Global:
      default:
        return this.globalTypeName;
    }
  }

  constructor(
    @Inject(WizardStepComponent)
    protected parent: WizardStepComponent,
    protected injector: Injector,
    private editRmmNotificationUtility: EditRmmNotificationUtility,
    private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService,
    private customAlertService: RmmCustomAlertingServiceController,
    private rmmNotificationService: RmmNotificationService,
    private i18n: I18NextService
  ) {
    this.customAlertingForm = this.editRmmNotificationUtility.getDefaultAlertCustomisationForm();
    this.customAlertService.usedSettings.pipe(take(1)).subscribe((value) => {
      this.customAlertingForm.patchValue(value ? value : this.editRmmNotificationUtility.getDefaultAlertCustomisationForm());
    });

    this.windowsAlertsEnabled = this.loadRmmCustomNotificationWizardService.windowsAlertsEnabled;
    this.showApplyToStep = loadRmmCustomNotificationWizardService.showApplyToStep;
    this.totalCount = this.loadRmmCustomNotificationWizardService.totalCount;
  }

  ngOnInit(): void {
    this.checkIfPickedSettingsAreConfigured();
    this.initProfileSearchByName();

    this.customAlertingForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.allGeneralAlertSelected = this.generalAlerts.every((key) => value[key]?.enabled);
      this.allWindowsAlertSelected = this.windowsAlerts.every((key) => value[key]?.enabled);

      this.customAlertService.usedSettings.next(cloneDeep(value));
      this.showCustomNotSelectAlert = !this.haveEnabledCustomAlerts(value);
      this.disableNext.emit(!this.applyToCorrect || !this.haveEnabledCustomAlerts(value));
    });

    this.form
      .get('action')
      .valueChanges.pipe(startWith(this.actions[0]), debounceTime(300), untilDestroyed(this))
      .subscribe((value) => {
        this.profiles = [];
        this.typeahead$.next(null);

        if (value.key === TypeOfSetting.Global) {
          this.customAlertService.setSettingsType(TypeOfSetting.Global);
        }

        this.enableLoadFromCompany = TypeOfSetting.Company === value.key;
        this.enableLoadFromComputer = TypeOfSetting.Computer === value.key;

        this.alertFromValue = this.i18n.t('mbs-ui:rmm-custom-alerting.wizard.firstStep.' + value.key);

        if (value.key === TypeOfSetting.Custom) {
          this.customAlertingForm.patchValue(
            this.showApplyToStep
              ? this.customAlertService.usedSettings.getValue()
              : this.editRmmNotificationUtility.getDefaultAlertCustomisationForm()
          );
          this.isNotConfigured = false;
          this.isCustom.emit(true);
        } else {
          this.isCustom.emit(false);
        }
      });

    this.form
      .get('profileId')
      .valueChanges.pipe(
        filter((value) => !!value),
        distinctUntilChanged(),
        tap((value) => this.customAlertService.loadCustom(value)),
        untilDestroyed(this)
      )
      .subscribe(noop);

    this.form.valueChanges
      .pipe(startWith({ action: this.actions[0], companySelection: null, computerSelection: null }), untilDestroyed(this))
      .subscribe((value) => {
        this.loading = true;
        this.isNotConfigured = false;
        this.isInherited = false;
        this.showCustomNotSelectAlert =
          value.action.key === TypeOfSetting.Custom && !this.haveEnabledCustomAlerts(this.customAlertingForm.value);
        this.wizardStepValidation(value);
      });

    if (this.showApplyToStep) this.setupProfile();
  }

  selectAllClicked(value: boolean, isGeneral: boolean) {
    const formValueList = isGeneral ? this.generalAlerts : this.windowsAlerts;

    formValueList.forEach((groupName, index) => {
      this.customAlertingForm
        .get(groupName)
        .get('enabled')
        .setValue(value, { emitEvent: index === formValueList.length - 1 });
    });

    this.customAlertService.usedSettings.next(cloneDeep(this.customAlertingForm.getRawValue()));
  }

  wizardStepValidation(
    value: Partial<{
      action: {
        id: number;
        key: TypeOfSetting;
        label: string;
      };
      profileId: string;
    }>
  ) {
    switch (value?.action?.key) {
      case TypeOfSetting.Company:
      case TypeOfSetting.Computer:
        this.canShowSummaryAndLink = !!value.profileId;
        this.disableNext.emit(!this.applyToCorrect || !value.profileId);
        break;
      case TypeOfSetting.Custom:
        this.canShowSummaryAndLink = false;
        this.disableNext.emit(!this.applyToCorrect || !this.haveEnabledCustomAlerts(this.customAlertingForm.value));
        break;
      case TypeOfSetting.Global:
      default:
        this.canShowSummaryAndLink = true;
        this.disableNext.emit(!this.applyToCorrect);
        break;
    }
  }

  disableNextForApply(disable: boolean) {
    this.applyToCorrect = !disable;
    this.wizardStepValidation(this.form.value);
  }

  checkIfPickedSettingsAreConfigured() {
    combineLatest([this.customAlertService.usedSettings, this.customAlertService.profilePostActions])
      .pipe(
        debounceTime(300),
        filter((settings, postActions) => !!settings),
        untilDestroyed(this)
      )
      .subscribe(([settings, postActions]) => {
        this.isNotConfigured = false;
        this.isInherited = false;

        this.loadRmmCustomNotificationWizardService.settingsData.next(cloneDeep(settings));

        this.loadRmmCustomNotificationWizardService.profileActionPayload.next(
          cloneDeep(postActions ?? this.loadRmmCustomNotificationWizardService.profileActionPayload.getValue())
        );
        this.loadRmmCustomNotificationWizardService.onPostActionsReLoaded.next(true);

        this.loading = false;
      });
  }

  haveEnabledCustomAlerts(alertValues: Partial<CustomAlertingSettings>) {
    if (!alertValues) return false;

    return Object.keys(alertValues).some(
      (key: RMMAlertTypes) => [...this.generalAlerts, ...this.windowsAlerts].includes(key) && alertValues[key]?.enabled
    );
  }

  setupProfile() {
    this.form.get('action').setValue(this.actions[3]);

    const settings = cloneDeep(this.customAlertService.customSettings);

    if (settings)
      Object.keys(settings).forEach((key) => {
        if (([...this.windowsAlerts.concat(this.generalAlerts)] as string[]).includes(key)) {
          this.customAlertingForm.get(key).get('enabled').setValue(settings[key]['enabled'], { emitEvent: true });
        }
      });
  }

  private initProfileSearchByName() {
    this.typeahead$
      .pipe(
        filter((value) => [TypeOfSetting.Company, TypeOfSetting.Computer].includes(this.form.get('action').value.key)),
        debounceTime(600),
        switchMap((value) => {
          const searchPayload: RmmNotificationsQueryParams = {
            pageNumber: 0,
            pageSize: 100,
            profileType:
              this.form.get('action').value.key === TypeOfSetting.Company
                ? NotificationProfileTypes.Company
                : NotificationProfileTypes.Computer
          };
          if (value) searchPayload.search = value;

          return this.rmmNotificationService.getProfileList(searchPayload);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (response) => {
          this.profiles = response.data;
          this.loading = false;
        },
        error: () => (this.loading = false)
      });
  }
}
